import { dedupeArray } from '@toggle/helpers';
import { formatTicker } from '@toggle/toggle';

import { useEntities } from '~/react-query-hooks/entities/useEntities';

import { normalizeFinancialStatementItems } from '../financial-statements-item/financialStatementUtils';
import type { ConversationInventory } from '../services/inventory-service';

export const useFormattedInventory = (
  inventory: ConversationInventory | undefined
): ConversationInventory => {
  const entityTags = inventory?.entity?.items?.map(i => i.tag) ?? [];
  const snakes = inventory?.timeseries?.items?.map(i => i.snake) ?? [];
  const financialStatementItems = normalizeFinancialStatementItems(
    inventory?.['financial-statement']?.items ?? []
  );
  const financialStatementsEntityTags = dedupeArray(
    financialStatementItems.map(item => item.entityTag)
  );

  const snakesEntityTags = dedupeArray(
    snakes.map(snake => snake.split('.')[0])
  );
  const uniqueEntityTags = dedupeArray(entityTags);
  const { data: snakesData } = useEntities({ entityTags: snakesEntityTags });
  const { data: entitiesData } = useEntities({ entityTags: uniqueEntityTags });
  const { data: financialStatementsData } = useEntities({
    entityTags: financialStatementsEntityTags,
  });

  const formattedTickers = entitiesData.map(formatTicker);
  const formattedSnakes = snakesData.map(formatTicker);
  const formattedTickerByEntityTag = Object.fromEntries(
    financialStatementsData.map(entity => [
      entity.tag,
      formatTicker(entity),
    ])
  );
  const formattedFinancialStatementItems = financialStatementItems.map(
    item => ({
      tag: formattedTickerByEntityTag[item.entityTag] ?? item.entityTag,
      statement: item.statement,
    })
  );

  return {
    ...inventory,
    ...(formattedTickers.length > 0 && {
      entity: {
        items: formattedTickers.map(ticker => ({ tag: ticker })),
      },
    }),
    ...(formattedSnakes.length > 0 && {
      timeseries: {
        items: formattedSnakes.map(snake => ({ snake })),
      },
    }),
    ...(formattedFinancialStatementItems.length > 0 && {
      ['financial-statement']: {
        items: formattedFinancialStatementItems,
      },
    }),
  };
};
